import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  XCircle,
} from 'lucide-react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import LetterPreviewModal from '../components/LetterPreviewModal';
import ConfirmModal from '../components/UI/ConfirmModal';
import FormSelect from '../components/UI/FormSelect';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { StatusBadge } from '../components/UI/StatusBadge';
import {
  DOCUMENT_REQUEST_PURPOSE_OPTIONS,
  DOCUMENT_REQUEST_TYPE_OPTIONS,
  getDocumentRequestPurposeLabel,
  getDocumentRequestType,
} from '../config/documentRequests';
import {
  readDocumentRequestError,
  useCancelDocumentRequest,
  useCreateDocumentRequest,
  useMyDocumentRequests,
  usePublishedDocumentTemplates,
} from '../hooks/useDocumentRequestData';
import { useMyHRLetters } from '../hooks/useTimelineData';
import api from '../services/api';
import toast from '../utils/toast';
import {
  formatDate,
  formatDateForInput,
  formatDateTime,
} from '../utils/dateUtils';
import { getLetterExpiryStatus } from '../utils/letterExpiryStatus';

const EMPTY_FORM = {
  letter_type: '',
  template: '',
  purpose: '',
  purpose_other: '',
};

const getRequestStatus = (request) => {
  if (request.status === 'submitted') {
    return { status: 'submitted', label: 'Pending HR review' };
  }
  if (request.status === 'approved' && !request.has_letter) {
    return { status: 'failed', label: 'Generation needs attention' };
  }
  return { status: request.status, label: undefined };
};

const LetterActions = ({
  request,
  downloading,
  onPreview,
  onDownload,
  onRenew,
  onCancel,
}) => {
  const expiry = request.has_letter
    ? getLetterExpiryStatus(
      request.letter_valid_until,
      request.letter_file_purged_at,
    )
    : null;

  if (request.status === 'submitted') {
    return (
      <button
        type="button"
        onClick={() => onCancel(request)}
        className="icon-button-danger"
        title="Cancel request"
        aria-label="Cancel request"
      >
        <XCircle className="w-4 h-4" />
      </button>
    );
  }

  if (request.status === 'approved' && request.has_letter && expiry?.canDownload) {
    return (
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPreview(request)}
          className="icon-button"
          title="Preview letter"
          aria-label="Preview letter"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onDownload(request)}
          disabled={downloading}
          className="icon-button"
          title="Download letter"
          aria-label="Download letter"
        >
          {downloading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Download className="w-4 h-4" />}
        </button>
      </div>
    );
  }

  if (request.status === 'approved' && request.has_letter && expiry && !expiry.canDownload) {
    return (
      <button
        type="button"
        onClick={() => onRenew(request)}
        className="btn btn-outline btn-sm"
      >
        <Plus className="w-4 h-4" />
        Request new
      </button>
    );
  }

  return null;
};

const RequestCard = (props) => {
  const { request } = props;
  const status = getRequestStatus(request);
  const expiry = request.has_letter
    ? getLetterExpiryStatus(
      request.letter_valid_until,
      request.letter_file_purged_at,
    )
    : null;

  return (
    <article className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {request.request_type_display
              || getDocumentRequestType(request.request_type).label}
          </p>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {getDocumentRequestPurposeLabel(request)}
          </p>
        </div>
        <StatusBadge
          status={status.status}
          label={status.label}
          showIcon
        />
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs text-gray-500">Requested</dt>
          <dd className="text-gray-900 mt-0.5 tabular-nums">
            {formatDate(request.created_at)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-gray-500">Letter validity</dt>
          <dd className="mt-0.5">
            {expiry
              ? (
                <StatusBadge
                  status={
                    expiry.purged
                      ? 'inactive'
                      : expiry.canDownload
                        ? (expiry.tone === 'expiring' ? 'pending' : 'verified')
                        : 'failed'
                  }
                  label={expiry.purged ? 'Removed' : expiry.label}
                />
              )
              : <span className="text-gray-500">Not issued</span>}
          </dd>
        </div>
      </dl>

      {request.status === 'rejected' && request.rejection_reason && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-800">HR feedback</p>
          <p className="text-sm text-red-700 mt-1">{request.rejection_reason}</p>
        </div>
      )}

      <div className="card-action-footer justify-end">
        <LetterActions {...props} />
      </div>
    </article>
  );
};

const SelfServiceLetters = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [cancelRequest, setCancelRequest] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [previewRequest, setPreviewRequest] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const submittingRef = useRef(false);

  const {
    data: requests = [],
    isLoading: requestsLoading,
    isError: requestsError,
    refetch: refetchRequests,
  } = useMyDocumentRequests();
  const { data: letters = [] } = useMyHRLetters(
    true,
    { refetchOnMount: 'always' },
  );
  const {
    data: templates = [],
    isLoading: templatesLoading,
    isError: templatesError,
  } = usePublishedDocumentTemplates(form.letter_type);
  const createRequest = useCreateDocumentRequest();
  const cancelMutation = useCancelDocumentRequest();

  const selectedType = getDocumentRequestType(form.letter_type);
  const expiringLetters = useMemo(
    () => letters.filter((letter) => {
      const expiry = getLetterExpiryStatus(
        letter.valid_until,
        letter.file_purged_at,
      );
      return expiry?.tone === 'expiring' && expiry.canDownload;
    }),
    [letters],
  );

  const updateLetterType = (letterType) => {
    const type = getDocumentRequestType(letterType);
    setForm({
      ...EMPTY_FORM,
      letter_type: letterType,
      purpose: type.purposeMode === 'fixed' ? type.purpose : '',
    });
  };

  const prepareSubmission = (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    const templateId = form.template
      || (templates.length === 1 ? String(templates[0].id) : '');
    const purpose = selectedType.purposeMode === 'fixed'
      ? selectedType.purpose
      : form.purpose;

    if (!form.letter_type) {
      toast.error('Select the letter you need');
      return;
    }
    if (!templateId) {
      toast.error('No published template is available for this letter');
      return;
    }
    if (!purpose) {
      toast.error('Select why you need this letter');
      return;
    }
    if (purpose === 'other' && !form.purpose_other.trim()) {
      toast.error('Describe why you need this letter');
      return;
    }

    setPendingSubmission({
      requestTypeLabel: selectedType.label,
      payload: {
        request_type: form.letter_type,
        template: templateId,
        purpose,
        purpose_other: purpose === 'other'
          ? form.purpose_other.trim()
          : null,
        idempotency_key: crypto.randomUUID(),
      },
    });
  };

  const submitRequest = async () => {
    if (!pendingSubmission || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await createRequest.mutateAsync(pendingSubmission.payload);
      setPendingSubmission(null);
      setForm(EMPTY_FORM);
    } catch {
      // Mutation owns the error toast; keep the confirmation open for retry.
    } finally {
      submittingRef.current = false;
    }
  };

  const confirmCancellation = async () => {
    if (!cancelRequest) return;
    try {
      await cancelMutation.mutateAsync({
        id: cancelRequest.id,
        reason: cancellationReason.trim(),
      });
      setCancelRequest(null);
      setCancellationReason('');
    } catch {
      // Mutation owns the error toast; keep the confirmation open for retry.
    }
  };

  const downloadLetter = async (request) => {
    if (!request.letter_id || downloadingId) return;
    setDownloadingId(request.letter_id);
    try {
      const response = await api.downloadHRLetter(request.letter_id);
      const url = URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${request.request_type || 'hr_letter'}_${formatDateForInput(request.letter_generated_at)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        await readDocumentRequestError(
          error,
          'Could not download the letter. Please try again.',
        ),
      );
      if (error?.response?.status === 410) refetchRequests();
    } finally {
      setDownloadingId(null);
    }
  };

  const requestRenewal = (request) => {
    const type = getDocumentRequestType(request.request_type);
    setForm({
      ...EMPTY_FORM,
      letter_type: request.request_type,
      purpose: type.purposeMode === 'fixed'
        ? type.purpose
        : request.purpose,
      purpose_other: request.purpose_other || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.info('A new request form has been prepared from your previous letter');
  };

  const actionProps = {
    downloading: false,
    onPreview: setPreviewRequest,
    onDownload: downloadLetter,
    onRenew: requestRenewal,
    onCancel: (request) => setCancelRequest(request),
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-4">
            <Link
              to="/self-service"
              aria-label="Back to Self-Service"
              title="Back to Self-Service"
              className="icon-button flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                Request Letters
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Ask HR for an official letter, then preview or download it here.
              </p>
            </div>
          </div>
        </div>

        {expiringLetters.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-amber-800" />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-amber-900">
                  {expiringLetters.length} letter{expiringLetters.length === 1 ? '' : 's'} expiring soon
                </h2>
                <p className="text-sm text-amber-800 mt-1">
                  Download a copy now or submit a fresh request before it expires.
                </p>
              </div>
            </div>
          </div>
        )}

        <section className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Plus className="w-5 h-5 text-blue-800" />
              </div>
              <div>
                <h2 className="text-subsection-header">New letter request</h2>
                <p className="text-body mt-1">
                  Choose the letter and tell HR what it will be used for.
                </p>
              </div>
            </div>
          </div>
          <div className="card-content">
            <form onSubmit={prepareSubmission} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Letter type *
                </label>
                <FormSelect
                  value={form.letter_type}
                  onChange={updateLetterType}
                  options={[
                    { value: '', label: 'Select the letter you need...' },
                    ...DOCUMENT_REQUEST_TYPE_OPTIONS,
                  ]}
                  theme="employee"
                  required
                />
                {form.letter_type && (
                  <p className="text-xs text-gray-500">
                    {selectedType.description}
                  </p>
                )}
              </div>

              {form.letter_type && templatesLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <LoadingSpinner size="sm" />
                  Checking available templates…
                </div>
              )}

              {form.letter_type && !templatesLoading && (templatesError || templates.length === 0) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      This letter is temporarily unavailable
                    </p>
                    <p className="text-sm text-amber-800 mt-1">
                      HR needs to publish a current template before requests can be submitted.
                    </p>
                  </div>
                </div>
              )}

              {form.letter_type && templates.length > 1 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Template *
                  </label>
                  <FormSelect
                    value={form.template}
                    onChange={(template) =>
                      setForm((current) => ({ ...current, template }))}
                    options={[
                      { value: '', label: 'Select a template...' },
                      ...templates.map((template) => ({
                        value: String(template.id),
                        label: `${template.document_type?.name || template.name}${template.version ? ` v${template.version}` : ''}`,
                      })),
                    ]}
                    theme="employee"
                    required
                  />
                </div>
              )}

              {form.letter_type && selectedType.purposeMode === 'select' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Purpose *
                  </label>
                  <FormSelect
                    value={form.purpose}
                    onChange={(purpose) =>
                      setForm((current) => ({ ...current, purpose }))}
                    options={[
                      { value: '', label: 'Why do you need this letter?' },
                      ...DOCUMENT_REQUEST_PURPOSE_OPTIONS,
                    ]}
                    theme="employee"
                    required
                  />
                </div>
              )}

              {form.letter_type && selectedType.purposeMode === 'fixed' && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Purpose
                  </p>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedType.purpose === 'pr_immigration'
                      ? 'Permanent residency or immigration application'
                      : 'Immigration notification for approved home-leave travel'}
                  </p>
                </div>
              )}

              {form.purpose === 'other' && (
                <div className="space-y-2">
                  <label
                    htmlFor="letter-purpose-other"
                    className="text-sm font-medium text-gray-700"
                  >
                    Describe the purpose *
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-gray-400" />
                    <textarea
                      id="letter-purpose-other"
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 min-h-[96px]"
                      placeholder="Briefly explain where this letter will be used..."
                      value={form.purpose_other}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          purpose_other: event.target.value,
                        }))}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={
                    createRequest.isPending
                    || templatesLoading
                    || (form.letter_type && templates.length === 0)
                  }
                  className="btn btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Review request
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-800" />
              </div>
              <div>
                <h2 className="text-subsection-header">My requests</h2>
                <p className="text-body mt-1">
                  Follow HR’s decision and access issued letters.
                </p>
              </div>
            </div>
          </div>
          <div className="card-content">
            {requestsLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : requestsError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Requests could not be loaded
                </h3>
                <p className="text-sm text-gray-500">
                  Try again. Your submitted requests have not been changed.
                </p>
                <button
                  type="button"
                  onClick={() => refetchRequests()}
                  className="btn btn-primary mt-4"
                >
                  Try again
                </button>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No letter requests yet
                </h3>
                <p className="text-sm text-gray-500">
                  Your requests and issued letters will appear here.
                </p>
              </div>
            ) : (
              <>
                <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {requests.map((request) => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      {...actionProps}
                      downloading={downloadingId === request.letter_id}
                    />
                  ))}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="table table-fixed min-w-[940px]">
                    <colgroup>
                      <col className="w-[16%]" />
                      <col className="w-[18%]" />
                      <col className="w-[19%]" />
                      <col className="w-[14%]" />
                      <col className="w-[17%]" />
                      <col className="w-[16%]" />
                    </colgroup>
                    <thead className="table-header">
                      <tr>
                        {['Status', 'Letter', 'Purpose', 'Requested', 'Validity', 'Actions'].map((heading) => (
                          <th
                            key={heading}
                            className={`table-header-cell ${heading === 'Actions' ? 'text-right' : ''}`}
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {requests.map((request) => {
                        const status = getRequestStatus(request);
                        const expiry = request.has_letter
                          ? getLetterExpiryStatus(
                            request.letter_valid_until,
                            request.letter_file_purged_at,
                          )
                          : null;
                        return (
                          <tr key={request.id} className="table-row">
                            <td className="table-cell">
                              <StatusBadge
                                status={status.status}
                                label={status.label}
                                showIcon
                              />
                            </td>
                            <td className="table-cell">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {request.request_type_display
                                  || getDocumentRequestType(request.request_type).label}
                              </p>
                              {request.letter_generated_at && (
                                <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
                                  Issued {formatDate(request.letter_generated_at)}
                                </p>
                              )}
                            </td>
                            <td className="table-cell">
                              <p className="text-sm text-gray-700 truncate">
                                {getDocumentRequestPurposeLabel(request)}
                              </p>
                              {request.status === 'rejected' && request.rejection_reason && (
                                <p
                                  className="text-xs text-red-600 mt-0.5 truncate"
                                  title={request.rejection_reason}
                                >
                                  {request.rejection_reason}
                                </p>
                              )}
                            </td>
                            <td className="table-cell text-sm text-gray-600 tabular-nums">
                              {formatDateTime(request.created_at)}
                            </td>
                            <td className="table-cell">
                              {expiry ? (
                                <StatusBadge
                                  status={
                                    expiry.purged
                                      ? 'inactive'
                                      : expiry.canDownload
                                        ? (expiry.tone === 'expiring' ? 'pending' : 'verified')
                                        : 'failed'
                                  }
                                  label={expiry.purged ? 'Removed' : expiry.label}
                                />
                              ) : (
                                <span className="text-sm text-gray-500">Not issued</span>
                              )}
                            </td>
                            <td className="table-cell text-right">
                              <LetterActions
                                request={request}
                                {...actionProps}
                                downloading={downloadingId === request.letter_id}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      <ConfirmModal
        isOpen={!!pendingSubmission}
        title="Submit this letter request?"
        confirmLabel="Submit to HR"
        cancelLabel="Go back"
        variant="primary"
        onConfirm={submitRequest}
        onCancel={() => setPendingSubmission(null)}
        isLoading={createRequest.isPending}
        theme="employee"
      >
        <div className="space-y-2 text-sm text-gray-700">
          <p>
            HR will review your{' '}
            <strong>{pendingSubmission?.requestTypeLabel}</strong> request.
          </p>
          <p>
            You will be notified when the letter is ready to preview and download.
          </p>
        </div>
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!cancelRequest}
        title="Cancel this letter request?"
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        variant="danger"
        onConfirm={confirmCancellation}
        onCancel={() => {
          setCancelRequest(null);
          setCancellationReason('');
        }}
        isLoading={cancelMutation.isPending}
        theme="employee"
      >
        <p className="text-sm text-gray-700">
          HR will no longer process this request. You can submit a new one later.
        </p>
        <div className="mt-4">
          <label
            htmlFor="cancel-letter-reason"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Reason (optional)
          </label>
          <textarea
            id="cancel-letter-reason"
            rows={3}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500"
            placeholder="Add context for HR..."
            value={cancellationReason}
            onChange={(event) => setCancellationReason(event.target.value)}
          />
        </div>
      </ConfirmModal>

      {previewRequest?.letter_id && (
        <LetterPreviewModal
          letterId={previewRequest.letter_id}
          source="hr_letter"
          letterTypeDisplay={
            previewRequest.request_type_display
            || getDocumentRequestType(previewRequest.request_type).label
          }
          onClose={() => setPreviewRequest(null)}
          onDownload={() => downloadLetter(previewRequest)}
          theme="employee"
          allLettersHref={null}
        />
      )}
    </DashboardLayout>
  );
};

export default SelfServiceLetters;
